import { cva } from "class-variance-authority"
import * as React from "react"
import { wrapperTypographyClassNames } from "../../typography"
import { cn } from "../../lib/utils"

export type BadgeVariant =
  | "default"
  | "destructive"
  | "success"
  | "warning"
  | "primary"

const badgeStyles = cva(
  `inline-flex max-w-max items-center justify-center gap-1 rounded-full border py-[3px] whitespace-nowrap transition-colors ${wrapperTypographyClassNames.badge}`,
  {
    variants: {
      variant: {
        default: "border-[#353e61] bg-[rgba(29,41,61,0.4)] text-foreground",
        destructive:
          "border-[#ff0018] bg-[rgba(239,68,68,0.2)] text-foreground",
        success: "border-[#22c55e] bg-[rgba(34,197,94,0.4)] text-white",
        warning: "border-[#fdae00] bg-[rgba(253,174,0,0.4)] text-foreground",
        primary: "border-[#1c62eb] bg-[rgba(28,98,235,0.4)] text-white",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const BADGE_PADDING_CLASSES = {
  left: "pl-[7px] pr-[9px]",
  none: "px-[11px]",
  right: "pl-[9px] pr-[7px]",
} as const

const SECONDARY_LABEL_CLASSES: Record<BadgeVariant, string> = {
  default: "text-muted-foreground",
  destructive: "text-[#ff0018]",
  success: "text-[#22c55e]",
  warning: "text-[#fdae00]",
  primary: "text-[#1c62eb]",
}

function getIconPosition({
  leftIcon,
  rightIcon,
}: {
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}) {
  if (leftIcon) {
    return "left"
  }

  if (rightIcon) {
    return "right"
  }

  return "none"
}

function capitalizeBadgeText(value: string) {
  return value.replace(/(\S)/, (match) => match.toLocaleUpperCase())
}

function formatBadgeNode(node: React.ReactNode): React.ReactNode {
  if (typeof node === "string") {
    return capitalizeBadgeText(node)
  }

  if (Array.isArray(node)) {
    return node.map(formatBadgeNode)
  }

  return node
}

export function badgeVariants({
  variant,
}: {
  variant?: BadgeVariant | null
} = {}) {
  return badgeStyles({
    variant: variant ?? "default",
  })
}

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  secondaryLabel?: React.ReactNode
  variant?: BadgeVariant
}

export function Badge({
  children,
  className,
  leftIcon,
  rightIcon,
  secondaryLabel,
  variant,
  ...props
}: BadgeProps) {
  const normalizedVariant = variant ?? "default"
  const iconPosition = getIconPosition({ leftIcon, rightIcon })

  return (
    <div
      className={cn(
        badgeVariants({ variant: normalizedVariant }),
        BADGE_PADDING_CLASSES[iconPosition],
        className
      )}
      {...props}
    >
      {leftIcon ? (
        <span className="shrink-0 [&>svg]:size-3">{leftIcon}</span>
      ) : null}
      {formatBadgeNode(children)}
      {secondaryLabel ? (
        <span
          className={cn(
            `shrink-0 ${wrapperTypographyClassNames.badgeSecondary}`,
            SECONDARY_LABEL_CLASSES[normalizedVariant]
          )}
        >
          {formatBadgeNode(secondaryLabel)}
        </span>
      ) : null}
      {rightIcon ? (
        <span className="shrink-0 [&>svg]:size-3">{rightIcon}</span>
      ) : null}
    </div>
  )
}
